import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { AppBar, Toolbar, Typography, List, ListItem, IconButton, Box, Button, Divider, Popper, Paper, ClickAwayListener, Container, Badge, Dialog, useMediaQuery, Drawer, Avatar } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import EditIcon from "@mui/icons-material/Edit";
import TocIcon from "@mui/icons-material/Toc";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AccountCircle from "@mui/icons-material/AccountCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import Link from "next/link";
// import { useRouter } from "next/router"; // Avoid using router during export (NextRouter not mounted)
import makeStyles from "@mui/styles/makeStyles";
import navbarsStyle from "@/styles/jss/nextjs-material-kit-pro/pages/componentsSections/navbarsStyle.js";
import SearchInput from "../CreateForm/SearchInput";
import SearchBar from "./SearchBar";
import { simpleForm as NewsletterForm } from "./Newsletter";
import { purple, green, blue, orange, yellow, gray, rose, background, common } from "@/theme/colors";
import TodoList from "@/components/Importable/TodoList";
import TaskModal from "@/components/Admin/TaskManager/TaskModal";
import { useUploadFileMutation, useUploadImageMutation } from "@/src/services/uploadApi";
import { api, useCreateWebsiteTaskMutation, useUpdateWebsiteTaskMutation, useGetAdminTasksQuery, useGetEventsQuery, useGetRegulationsQuery, useGetArticlesQuery } from "@/src/services/api";
import { useAppDispatch } from "@/src/store/hooks";
import { assetUrl } from "@/utils/asset";
import { useIndexSideNav } from "@/hooks/useIndexSideNav";
import SkipLink from "@/components/Importable/SkipLink";
import { useAuth } from "@/context/AuthContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { knownSectionLabels, pageLabels } from "@/components/sections/registry";

/** Remove the hover-outline data-attribute and restore position if we set it */
function pcClearOutline(el) {
  el.removeAttribute("data-pc-hover-outline");
  el.removeAttribute("data-pc-hover-promote-z");
  if (el.getAttribute("data-pc-pos-was-static") === "1") {
    el.style.position = "";
    el.removeAttribute("data-pc-pos-was-static");
  }
}
function pcClearAllOutlines() {
  document.querySelectorAll('[data-pc-hover-outline="1"]').forEach(pcClearOutline);
}
/** Apply the hover outline, setting position:relative only on static elements */
function pcApplyOutline(el) {
  const cs = getComputedStyle(el);
  if (cs.position === "static") {
    el.style.position = "relative";
    el.setAttribute("data-pc-pos-was-static", "1");
  }
  // Only promote elements without an explicit stacking context up to z=100
  // so the dashed outline floats above sibling overlays. Elements that
  // already sit on a higher layer (e.g. the editor Drawer at z=1200) must
  // keep their natural z-index, otherwise the navbar (z≥1100) ends up
  // covering them while the selector tool is active.
  // Also respect data-pc-no-promote-z to opt specific elements out entirely.
  const rawZ = cs.zIndex;
  const numericZ = parseInt(rawZ, 10);
  const hasOwnStacking = Number.isFinite(numericZ) && numericZ >= 100;
  const optedOut = el.hasAttribute("data-pc-no-promote-z");
  if (!hasOwnStacking && !optedOut) {
    el.setAttribute("data-pc-hover-promote-z", "1");
  }
  el.setAttribute("data-pc-hover-outline", "1");
}

/** Map a live pathname (e.g. "/events/some-slug") to its registry pattern (e.g. "/events/[id]"). */
function resolvePagePattern(pathname) {
  // Exact match first
  if (pageLabels[pathname]) return pathname;
  // Strip trailing slash for matching
  const normalized = pathname.replace(/\/$/, "") || "/";
  if (pageLabels[normalized]) return normalized;
  // Try known patterns with dynamic segments
  const segments = normalized.split("/").filter(Boolean);
  // Pattern: /<base>/<slug> → /<base>/[id]
  if (segments.length >= 2) {
    const base = "/" + segments.slice(0, -1).join("/");
    const pattern = base + "/[id]";
    if (pageLabels[pattern]) return pattern;
  }
  // Fallback: return the raw pathname
  return normalized;
}
import { useGetOrganizationsQuery } from "@/src/services/organizationsApi";
import { useDataVersionCheck } from "@/hooks/useDataVersionCheck";
import { searchSite } from "@/utils/siteSearch";
import SiteSearchModal from "./SiteSearchModal";
import { useAccountMenu } from "./useAccountMenu";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const useStyles = makeStyles(navbarsStyle);
export function NavBar({}) {
  // const router = useRouter();
  const navigate = path => {
    if (typeof window !== "undefined") {
      try {
        const href = assetUrl(path);
        window.location.assign(href);
      } catch {
        // noop
      }
    }
  };
  const classes = useStyles(); // eslint-disable-line @typescript-eslint/no-unused-vars
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFilter, setSearchFilter] = useState("all"); // eslint-disable-line @typescript-eslint/no-unused-vars
  const searchButtonRef = useRef(null);
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };
  const toggleSearchDropdown = () => {
    setSearchDropdownOpen(!searchDropdownOpen);
  };
  const handleSearchClear = () => {
    // eslint-disable-line @typescript-eslint/no-unused-vars
    setSearchQuery("");
  };
  const handleSearchSubmit = () => {
    // eslint-disable-line @typescript-eslint/no-unused-vars
    // Handle search functionality here
    setSearchDropdownOpen(false);
  };
  const handleSearchClose = () => {
    setSearchDropdownOpen(false);
  };
  const {
    isAuthenticated,
    user
  } = useAuth();
  const {
    settings: siteSettings
  } = useSiteSettings();
  const isAdmin = user?.role === "admin";
  const accountMenu = useAccountMenu();
  const navUserName = {
    first: user?.profile?.firstName || user?.displayName?.split(" ")[0] || "",
    last: user?.profile?.lastName || (user?.displayName ? user.displayName.split(" ").slice(1).join(" ") : "")
  };

  // Request Edits state
  const [captureMode, setCaptureMode] = useState(false);
  const [todoListOpen, setTodoListOpen] = useState(false);
  const [todoListEnabled, setTodoListEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("todoListEnabled") === "true";
  });
  const [todoUnresolved, setTodoUnresolved] = useState(() => {
    if (typeof window === "undefined") return 0;
    try {
      const raw = localStorage.getItem("todoListItems");
      const arr = raw ? JSON.parse(raw) : [];
      return arr.filter(i => !i.resolved).length;
    } catch {
      return 0;
    }
  });

  // Read todoListEnabled from localStorage on mount + listen for changes
  useEffect(() => {
    const sync = () => {
      const enabled = localStorage.getItem("todoListEnabled") === "true";
      setTodoListEnabled(enabled);
      try {
        const raw = localStorage.getItem("todoListItems");
        const arr = raw ? JSON.parse(raw) : [];
        setTodoUnresolved(arr.filter(i => !i.resolved).length);
      } catch {
        setTodoUnresolved(0);
      }
    };
    sync();
    window.addEventListener("todoListChanged", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("todoListChanged", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  const [hoverTarget, setHoverTarget] = useState(null);
  const [taskModalOpen, setRequestedModalOpen] = useState(false);
  const [captureData, setCaptureData] = useState(null);
  const [captureSaving, setCaptureSaving] = useState(false);
  const [captureError, setCaptureError] = useState(null);
  const [uploadImage] = useUploadImageMutation();
  const [uploadFile] = useUploadFileMutation();
  const [createWebEdit] = useCreateWebsiteTaskMutation();
  const [updateWebEdit] = useUpdateWebsiteTaskMutation();
  const currentPagePath = typeof window !== "undefined" ? resolvePagePattern(window.location.pathname) : "";

  // Admin-gated to match the BE (the whole /admin/tasks controller is
  // AdminGuard) — a non-admin firing this per page just buys a 403.
  const {
    data: pageTasks = []
  } = useGetAdminTasksQuery(isAdmin ? {
    pagePath: currentPagePath,
    status: "OPEN"
  } : undefined, {
    skip: !isAdmin
  });

  // requestedCount removed — badge now shows todoUnresolved count

  // Auto-open Requested Edit dialog from deep link (?reId=... or ?reSel=...)
  React.useEffect(() => {
    if (!isAuthenticated) return;
    if (typeof window === "undefined") return;
    if (!pageTasks.length) return;
    try {
      const url = new URL(window.location.href);
      const reId = url.searchParams.get("reId");
      const reSel = url.searchParams.get("reSel");
      if (!reId && !reSel) return;
      let match;
      if (reId) match = pageTasks.find(x => x.id === reId);
      if (!match && reSel) match = pageTasks.find(x => x.elementSelector === reSel);
      if (match) {
        setCaptureData({
          // Carry the existing task id: without it the capture
          // dialog's Save would CREATE a duplicate instead of
          // editing the task the deep link points at.
          id: match.id,
          pagePath: match.pagePath,
          sectionLabel: match.label || "",
          elementSelector: match.elementSelector,
          elementText: match.elementText ?? undefined,
          priority: match.priority ?? 2,
          body: match.body ?? ""
        });
        setRequestedModalOpen(true);
        const el = document.querySelector(match.elementSelector);
        // Backward compat: old tasks may have [aria-label="..."] selectors
        const resolvedEl = el ?? (() => {
          if (match.elementSelector.startsWith('[aria-label="')) {
            const label = match.elementSelector.slice(13, -2);
            return document.querySelector(`[data-select-target="${label}"]`);
          }
          return null;
        })();
        if (resolvedEl) {
          resolvedEl.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
          pcApplyOutline(resolvedEl);
          setTimeout(() => {
            pcClearOutline(resolvedEl);
          }, 1200);
        }
      }
    } catch {}
  }, [isAuthenticated, pageTasks]);
  const handleCaptureToggle = () => {
    if (!isAuthenticated) return;
    // If captureMode is already on, a second click opens the todo list instead
    if (captureMode) {
      setCaptureMode(false);
      setTodoListOpen(true);
      if (hoverTarget) {
        try {
          pcClearOutline(hoverTarget);
        } catch {}
      }
      setHoverTarget(null);
      return;
    }
    setCaptureMode(true);
    if (hoverTarget) {
      try {
        pcClearOutline(hoverTarget);
      } catch {}
    }
    setHoverTarget(null);
  };

  // Find the nearest ancestor (or self) whose data-select-target is in the section registry
  const findOutlineTarget = React.useCallback(startEl => {
    let node = startEl;
    while (node) {
      const label = node.getAttribute?.("data-select-target");
      if (label && knownSectionLabels.has(label)) return node;
      node = node.parentElement;
    }
    return null;
  }, []);

  // Hover outline while in capture mode
  React.useEffect(() => {
    if (!captureMode) {
      // Clear any leftover outlines when exiting capture mode
      try {
        pcClearAllOutlines();
      } catch {}
      if (hoverTarget) setHoverTarget(null);
      return;
    }
    const handleOver = e => {
      // Normalize to an element node
      let el = e.target;
      while (el && el.nodeType !== 1) {
        el = el.parentElement;
      }
      if (!el) return;
      const target = findOutlineTarget(el);
      // Clear previous outlines
      try {
        pcClearAllOutlines();
      } catch {}
      // Only outline if we found a recognized section
      if (!target) {
        setHoverTarget(null);
        return;
      }
      try {
        pcApplyOutline(target);
      } catch {}
      setHoverTarget(target);
    };
    const handleClick = e => {
      e.preventDefault();
      e.stopPropagation();
      const el = e.target;
      if (!el) return;
      const target = findOutlineTarget(el);
      if (!target) return; // ignore clicks outside recognized sections
      const sectionLabel = target.getAttribute("data-select-target") || "";
      const page = resolvePagePattern(window.location.pathname);
      const snippet = (target.textContent || "").trim().slice(0, 120);
      // Collect captured data and open TaskModal instead of creating immediately
      setCaptureData({
        pagePath: page,
        sectionLabel,
        elementSelector: `[data-select-target="${sectionLabel}"]`,
        elementText: snippet,
        priority: 2,
        body: ""
      });
      setCaptureError(null);
      setRequestedModalOpen(true);
      setCaptureMode(false);
      try {
        pcClearAllOutlines();
      } catch {}
      setHoverTarget(null);
    };
    const handleKeyDown = e => {
      if (e.key === "Escape" || e.keyCode === 27) {
        e.preventDefault();
        e.stopPropagation();
        try {
          pcClearAllOutlines();
        } catch {}
        setHoverTarget(null);
        setCaptureMode(false);
      }
    };
    window.addEventListener("mouseover", handleOver, true);
    window.addEventListener("click", handleClick, true);
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("mouseover", handleOver, true);
      window.removeEventListener("click", handleClick, true);
      window.removeEventListener("keydown", handleKeyDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureMode, hoverTarget, theme.breakpoints.values, findOutlineTarget]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SkipLink, {}), /*#__PURE__*/_jsx("style", {
      children: `
                [data-pc-hover-promote-z="1"],
                [data-pc-re-outline="1"]:not([data-pc-no-promote-z]) {
                    z-index: 100 !important;
                }
                [data-pc-hover-outline="1"]::after,
                [data-pc-re-outline="1"]::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    border: 5px dashed gold;
                    pointer-events: none;
                    z-index: 2147483646;
                }
            `
    }), /*#__PURE__*/_jsxs(AppBar, {
      position: "fixed",
      color: "default",
      sx: {
        backgroundColor: isMobile ? blue.primary : "white",
        color: isMobile ? `${blue.contrast} !important` : undefined,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
      },
      "data-pc-outline": "navbar",
      "data-select-target": "Navigation Bar",
      children: [/*#__PURE__*/_jsx(Toolbar, {
        sx: {
          minHeight: 64
        },
        disableGutters: true,
        children: isMobile ?
        /*#__PURE__*/
        // Mobile Layout
        _jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Box, {
            sx: {
              display: "flex",
              alignItems: "center",
              gap: 1,
              height: "64px"
            },
            children: /*#__PURE__*/_jsx(IconButton, {
              edge: "start",
              color: "inherit",
              "aria-label": "open navigation menu",
              onClick: toggleMobileMenu,
              size: "small",
              sx: {
                p: 0.75,
                backgroundColor: "transparent",
                borderRadius: 2,
                transition: "background-color 120ms ease",
                ml: "10px",
                "&:hover": {
                  backgroundColor: "transparent"
                },
                "&:active": {
                  backgroundColor: "transparent"
                },
                "&:focus-visible": {
                  outline: "2px solid rgba(255,255,255,0.85)",
                  outlineOffset: 2
                }
              },
              children: /*#__PURE__*/_jsx(MenuIcon, {
                sx: {
                  color: "#fff"
                }
              })
            })
          }), /*#__PURE__*/_jsx(Typography, {
            variant: "h6",
            component: "div",
            sx: {
              flexGrow: 1,
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
              [theme.breakpoints.down("md")]: {
                fontSize: "1.5rem"
              },
              [theme.breakpoints.down("sm")]: {
                fontSize: "1.1rem"
              },
              "@media (max-width: 375px)": {
                fontSize: "0.95rem"
              },
              "@media (max-width: 320px)": {
                fontSize: "0.85rem"
              }
            },
            children: /*#__PURE__*/_jsx(Box, {
              component: Link,
              href: "/",
              sx: {
                backgroundColor: "transparent",
                color: `${blue.contrast} !important`,
                px: 2,
                py: 0,
                borderRadius: 0,
                textDecoration: "none",
                height: 64,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box"
              },
              children: "Product Certification"
            })
          }), isAuthenticated && user?.role === "admin" && /*#__PURE__*/_jsxs(Box, {
            sx: {
              display: "none",
              alignItems: "center"
            },
            children: [/*#__PURE__*/_jsx(IconButton, {
              color: captureMode ? "primary" : "inherit",
              "aria-label": "toggle capture tasks",
              onClick: handleCaptureToggle,
              size: "small",
              sx: {
                ml: 0.5
              },
              children: /*#__PURE__*/_jsx(EditIcon, {})
            }), todoListEnabled && /*#__PURE__*/_jsx(IconButton, {
              color: "inherit",
              "aria-label": "toggle todo list",
              onClick: () => setTodoListOpen(p => !p),
              size: "small",
              children: /*#__PURE__*/_jsx(Badge, {
                badgeContent: todoUnresolved,
                color: "warning",
                children: /*#__PURE__*/_jsx(TocIcon, {})
              })
            })]
          }), /*#__PURE__*/_jsxs(IconButton, {
            color: "inherit",
            size: "small",
            onClick: accountMenu.openMenu,
            sx: {
              mr: "10px"
            },
            "aria-label": "account menu",
            children: [isAuthenticated ? /*#__PURE__*/_jsx(Badge, {
              badgeContent: accountMenu.notifCount,
              color: "error",
              children: /*#__PURE__*/_jsx(Avatar, {
                src: user?.profileImageUrl || undefined,
                sx: {
                  width: 24,
                  height: 24
                },
                children: /*#__PURE__*/_jsx(AccountCircle, {
                  sx: {
                    width: 20,
                    height: 20
                  }
                })
              })
            }) : /*#__PURE__*/_jsx(AccountCircle, {}), accountMenu.profileMenuOpen ? /*#__PURE__*/_jsx(ExpandLessIcon, {
              sx: {
                fontSize: 16
              }
            }) : /*#__PURE__*/_jsx(ExpandMoreIcon, {
              sx: {
                fontSize: 16
              }
            })]
          })]
        }) :
        /*#__PURE__*/
        // Desktop Layout
        _jsx(_Fragment, {
          children: /*#__PURE__*/_jsxs(Container, {
            maxWidth: false,
            disableGutters: true,
            style: {
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              marginLeft: 0,
              paddingLeft: 0,
              paddingRight: 20
            },
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "h6",
              component: "div",
              sx: {
                flex: "0 0 auto",
                [theme.breakpoints.down("sm")]: {
                  fontSize: "0.95rem"
                },
                "@media (max-width: 375px)": {
                  fontSize: "0.875rem"
                }
              },
              children: /*#__PURE__*/_jsx(Box, {
                component: Link,
                href: "/",
                sx: {
                  backgroundColor: blue.primary,
                  color: `${blue.contrast} !important`,
                  px: 2,
                  py: 0,
                  borderRadius: 0,
                  textDecoration: "none",
                  height: 64,
                  width: 320,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxSizing: "border-box",
                  mr: 1.5
                },
                children: "Product Certification"
              })
            }), /*#__PURE__*/_jsxs(Box, {
              sx: {
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2
              },
              children: [/*#__PURE__*/_jsxs(Box, {
                sx: {
                  display: "flex",
                  alignItems: "center",
                  gap: 2
                },
                children: [/*#__PURE__*/_jsx(ListItem, {
                  sx: {
                    flex: 0,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    px: 1.25,
                    "& a": {
                      color: gray.dark,
                      fontWeight: 600,
                      textDecoration: "none"
                    },
                    "& a:hover": {
                      color: gray.primary
                    }
                  },
                  children: /*#__PURE__*/_jsx(Link, {
                    href: "/",
                    children: "Home"
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  sx: {
                    flex: 0,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    px: 1.25,
                    "& a": {
                      color: orange.primary,
                      fontWeight: 600,
                      textDecoration: "none"
                    },
                    "& a:hover": {
                      color: orange.light
                    }
                  },
                  children: /*#__PURE__*/_jsx(Link, {
                    href: "/organizations",
                    children: "Organizations"
                  })
                }), siteSettings.eventsVisible ? /*#__PURE__*/_jsx(ListItem, {
                  sx: {
                    flex: 0,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    px: 1.25,
                    "& a": {
                      color: green.primary,
                      fontWeight: 600,
                      textDecoration: "none"
                    },
                    "& a:hover": {
                      color: green.light
                    }
                  },
                  children: /*#__PURE__*/_jsx(Link, {
                    href: "/events",
                    children: "Events"
                  })
                }) : null, siteSettings.regulationsVisible ? /*#__PURE__*/_jsx(ListItem, {
                  sx: {
                    flex: 0,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    px: 1.25,
                    "& a": {
                      color: blue.primary,
                      fontWeight: 600,
                      textDecoration: "none"
                    },
                    "& a:hover": {
                      color: blue.light
                    }
                  },
                  children: /*#__PURE__*/_jsx(Link, {
                    href: "/regulations",
                    children: "Regulations"
                  })
                }) : null, siteSettings.articlesVisible ? /*#__PURE__*/_jsx(ListItem, {
                  sx: {
                    flex: 0,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    px: 1.25,
                    "& a": {
                      color: purple.primary,
                      fontWeight: 600,
                      textDecoration: "none"
                    },
                    "& a:hover": {
                      color: purple.light
                    }
                  },
                  children: /*#__PURE__*/_jsx(Link, {
                    href: "/articles",
                    children: "Articles"
                  })
                }) : null, siteSettings.professionalsVisible ? /*#__PURE__*/_jsx(ListItem, {
                  sx: {
                    flex: 0,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    px: 1.25,
                    "& a": {
                      color: rose.primary,
                      fontWeight: 600,
                      textDecoration: "none"
                    },
                    "& a:hover": {
                      color: rose.light
                    }
                  },
                  children: /*#__PURE__*/_jsx(Link, {
                    href: "/professionals",
                    children: "Professionals"
                  })
                }) : null]
              }), /*#__PURE__*/_jsxs(Box, {
                sx: {
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                },
                children: [isAuthenticated && user?.role === "admin" && /*#__PURE__*/_jsxs(_Fragment, {
                  children: [/*#__PURE__*/_jsx(IconButton, {
                    color: captureMode ? "primary" : "inherit",
                    "aria-label": "toggle capture tasks",
                    onClick: handleCaptureToggle,
                    size: "small",
                    children: /*#__PURE__*/_jsx(EditIcon, {})
                  }), todoListEnabled && /*#__PURE__*/_jsx(IconButton, {
                    color: "inherit",
                    "aria-label": "toggle todo list",
                    onClick: () => setTodoListOpen(p => !p),
                    size: "small",
                    children: /*#__PURE__*/_jsx(Badge, {
                      badgeContent: todoUnresolved,
                      color: "warning",
                      children: /*#__PURE__*/_jsx(TocIcon, {})
                    })
                  })]
                }), isAuthenticated ? /*#__PURE__*/_jsxs(Button, {
                  size: "small",
                  onClick: accountMenu.openMenu,
                  "aria-label": "account menu",
                  endIcon: /*#__PURE__*/_jsxs(Box, {
                    sx: {
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5
                    },
                    children: [/*#__PURE__*/_jsx(Badge, {
                      badgeContent: accountMenu.notifCount,
                      color: "error",
                      children: /*#__PURE__*/_jsx(Avatar, {
                        src: user?.profileImageUrl || undefined,
                        sx: {
                          width: 24,
                          height: 24
                        },
                        children: /*#__PURE__*/_jsx(AccountCircle, {
                          sx: {
                            width: 20,
                            height: 20
                          }
                        })
                      })
                    }), accountMenu.profileMenuOpen ? /*#__PURE__*/_jsx(ExpandLessIcon, {
                      fontSize: "small"
                    }) : /*#__PURE__*/_jsx(ExpandMoreIcon, {
                      fontSize: "small"
                    })]
                  }),
                  sx: {
                    color: "inherit",
                    textTransform: "none",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    mr: 2,
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.06)"
                    }
                  },
                  children: [/*#__PURE__*/_jsxs(Box, {
                    component: "span",
                    sx: {
                      display: {
                        xs: "none",
                        md: "inline"
                      }
                    },
                    children: [navUserName.first, navUserName.last ? ` ${navUserName.last}` : ""]
                  }), /*#__PURE__*/_jsx(Box, {
                    component: "span",
                    sx: {
                      display: {
                        xs: "inline",
                        md: "none"
                      }
                    },
                    children: navUserName.first
                  })]
                }) : /*#__PURE__*/_jsx(IconButton, {
                  color: "inherit",
                  size: "small",
                  onClick: accountMenu.openMenu,
                  sx: {
                    mr: 2
                  },
                  children: /*#__PURE__*/_jsx(AccountCircle, {})
                })]
              })]
            })]
          })
        })
      }), /*#__PURE__*/_jsx(Drawer, {
        anchor: "left",
        open: mobileMenuOpen,
        onClose: handleMobileMenuClose,
        PaperProps: {
          sx: {
            backgroundColor: background.secondary
          }
        },
        children: /*#__PURE__*/_jsx(Box, {
          sx: {
            width: 250
          },
          children: /*#__PURE__*/_jsxs(List, {
            children: [/*#__PURE__*/_jsx(ListItem, {
              children: /*#__PURE__*/_jsx(Link, {
                href: "/",
                style: {
                  textDecoration: "none"
                },
                children: "Home"
              })
            }), /*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsx(ListItem, {
              sx: {
                "& a": {
                  color: gray.dark,
                  fontWeight: 600,
                  textDecoration: "none"
                }
              },
              children: /*#__PURE__*/_jsx(Link, {
                href: "/",
                children: "Home"
              })
            }), /*#__PURE__*/_jsx(ListItem, {
              sx: {
                "& a": {
                  color: orange.primary,
                  fontWeight: 600,
                  textDecoration: "none"
                }
              },
              children: /*#__PURE__*/_jsx(Link, {
                href: "/organizations",
                children: "Organizations"
              })
            }), siteSettings.eventsVisible && /*#__PURE__*/_jsx(ListItem, {
              sx: {
                "& a": {
                  color: green.primary,
                  fontWeight: 600,
                  textDecoration: "none"
                }
              },
              children: /*#__PURE__*/_jsx(Link, {
                href: "/events",
                children: "Events"
              })
            }), siteSettings.regulationsVisible && /*#__PURE__*/_jsx(ListItem, {
              sx: {
                "& a": {
                  color: blue.primary,
                  fontWeight: 600,
                  textDecoration: "none"
                }
              },
              children: /*#__PURE__*/_jsx(Link, {
                href: "/regulations",
                children: "Regulations"
              })
            }), siteSettings.articlesVisible && /*#__PURE__*/_jsx(ListItem, {
              sx: {
                "& a": {
                  color: purple.primary,
                  fontWeight: 600,
                  textDecoration: "none"
                }
              },
              children: /*#__PURE__*/_jsx(Link, {
                href: "/articles",
                children: "Articles"
              })
            }), siteSettings.professionalsVisible && /*#__PURE__*/_jsx(ListItem, {
              sx: {
                "& a": {
                  color: rose.primary,
                  fontWeight: 600,
                  textDecoration: "none"
                }
              },
              children: /*#__PURE__*/_jsx(Link, {
                href: "/professionals",
                children: "Professionals"
              })
            }), /*#__PURE__*/_jsx(ListItem, {
              sx: {
                px: 2
              },
              children: /*#__PURE__*/_jsx(Button, {
                onClick: accountMenu.openMenu,
                variant: "outlined",
                fullWidth: true,
                size: "large",
                startIcon: isAuthenticated ? undefined : /*#__PURE__*/_jsx(AccountCircle, {}),
                endIcon: isAuthenticated ? /*#__PURE__*/_jsxs(Box, {
                  sx: {
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5
                  },
                  children: [/*#__PURE__*/_jsx(Badge, {
                    badgeContent: accountMenu.notifCount,
                    color: "error",
                    children: /*#__PURE__*/_jsx(Avatar, {
                      src: user?.profileImageUrl || undefined,
                      sx: {
                        width: 28,
                        height: 28
                      },
                      children: /*#__PURE__*/_jsx(AccountCircle, {
                        sx: {
                          width: 24,
                          height: 24
                        }
                      })
                    })
                  }), accountMenu.profileMenuOpen ? /*#__PURE__*/_jsx(ExpandLessIcon, {}) : /*#__PURE__*/_jsx(ExpandMoreIcon, {})]
                }) : undefined,
                sx: {
                  justifyContent: "flex-start",
                  textTransform: "none",
                  borderColor: theme => theme.palette.divider,
                  color: theme => theme.palette.text.primary,
                  gap: 1,
                  py: 1.5,
                  minHeight: 64
                },
                children: isAuthenticated ? /*#__PURE__*/_jsxs(_Fragment, {
                  children: [/*#__PURE__*/_jsxs(Box, {
                    component: "span",
                    sx: {
                      display: {
                        xs: "none",
                        md: "inline"
                      }
                    },
                    children: [navUserName.first, navUserName.last ? ` ${navUserName.last}` : ""]
                  }), /*#__PURE__*/_jsx(Box, {
                    component: "span",
                    sx: {
                      display: {
                        xs: "inline",
                        md: "none"
                      }
                    },
                    children: navUserName.first || "Account"
                  })]
                }) : "Account"
              })
            })]
          })
        })
      }), /*#__PURE__*/_jsx(Popper, {
        open: searchDropdownOpen,
        anchorEl: searchButtonRef.current,
        placement: "bottom-end",
        style: {
          zIndex: 1300
        },
        children: /*#__PURE__*/_jsx(ClickAwayListener, {
          onClickAway: handleSearchClose,
          children: /*#__PURE__*/_jsx(Paper, {
            sx: {
              p: 2,
              minWidth: 300
            },
            children: /*#__PURE__*/_jsx(SearchInput, {})
          })
        })
      })]
    }), /*#__PURE__*/_jsx(TaskModal, {
      open: taskModalOpen,
      onClose: () => {
        setRequestedModalOpen(false);
        setCaptureData(null);
        setCaptureError(null);
      },
      mode: "capture",
      initialData: captureData || undefined,
      saving: captureSaving,
      error: captureError,
      onSave: async data => {
        setCaptureSaving(true);
        setCaptureError(null);
        try {
          if (data.id) {
            // Deep-linked existing task: UPDATE it rather
            // than create a duplicate.
            await updateWebEdit({
              id: data.id,
              label: data.sectionLabel,
              priority: data.priority,
              body: data.body.trim() || undefined,
              subtasks: data.subtasks
            }).unwrap();
          } else {
            await createWebEdit({
              pagePath: data.pagePath,
              elementSelector: data.elementSelector,
              elementText: data.elementText || "",
              label: data.sectionLabel,
              priority: data.priority,
              body: data.body.trim() || undefined,
              screenshots: data.screenshots?.map(s => ({
                thumbnailUrl: s.thumbnailUrl,
                mediumUrl: s.mediumUrl,
                originalUrl: s.originalUrl
              })),
              subtasks: data.subtasks,
              attachments: data.attachments
            }).unwrap();
          }
          setRequestedModalOpen(false);
          setCaptureData(null);
        } catch (err) {
          setCaptureError(err?.data?.message || err?.message || "Failed to save task");
        } finally {
          setCaptureSaving(false);
        }
      },
      onUploadScreenshot: async file => {
        const res = await uploadImage({
          file,
          folder: "tasks",
          entityType: "task-screenshot"
        }).unwrap();
        return {
          thumbnailUrl: res.thumbnail,
          mediumUrl: res.medium,
          originalUrl: res.original
        };
      },
      onUploadFile: async file => {
        return uploadFile({
          file,
          folder: "tasks"
        }).unwrap();
      }
    }), /*#__PURE__*/_jsx(TodoList, {
      open: todoListOpen,
      onClose: () => setTodoListOpen(false)
    }), accountMenu.elements]
  });
}

// ---------------------------------------------------------------------------
// SearchNavBar — alternate navigation layout with a 3-row search-style header.
//   Row 1: dark gray utility bar (Newsletter on the left, account on the right)
//   Row 2: logo + name, autocomplete search input, "Get Certification Help" box
//   Row 3: primary nav links (Home, Organizations, Events, Regulations, Articles)
// ---------------------------------------------------------------------------

export function SearchNavBar({}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    isAuthenticated,
    user
  } = useAuth();
  const accountMenu = useAccountMenu();
  const {
    settings: siteSettings
  } = useSiteSettings();
  const isAdmin = user?.role === "admin";
  const snUserName = {
    first: user?.profile?.firstName || user?.displayName?.split(" ")[0] || "",
    last: user?.profile?.lastName || (user?.displayName ? user.displayName.split(" ").slice(1).join(" ") : "")
  };

  // ── Selector tool (admin only) ─────────────────────────────────────
  const [captureMode, setCaptureMode] = useState(false);
  const [todoListOpen, setTodoListOpen] = useState(false);
  const [todoListEnabled, setTodoListEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("todoListEnabled") === "true";
  });
  const [todoUnresolved, setTodoUnresolved] = useState(() => {
    if (typeof window === "undefined") return 0;
    try {
      const raw = localStorage.getItem("todoListItems");
      const arr = raw ? JSON.parse(raw) : [];
      return arr.filter(i => !i.resolved).length;
    } catch {
      return 0;
    }
  });
  useEffect(() => {
    const sync = () => {
      const enabled = localStorage.getItem("todoListEnabled") === "true";
      setTodoListEnabled(enabled);
      try {
        const raw = localStorage.getItem("todoListItems");
        const arr = raw ? JSON.parse(raw) : [];
        setTodoUnresolved(arr.filter(i => !i.resolved).length);
      } catch {
        setTodoUnresolved(0);
      }
    };
    sync();
    window.addEventListener("todoListChanged", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("todoListChanged", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  const [hoverTarget, setHoverTarget] = useState(null);
  const [taskModalOpen, setSearchNavTaskModalOpen] = useState(false);
  const [captureData, setCaptureData] = useState(null);
  const [captureSaving, setCaptureSaving] = useState(false);
  const [captureError, setCaptureError] = useState(null);
  const [uploadImageSN] = useUploadImageMutation();
  const [uploadFileSN] = useUploadFileMutation();
  const [createWebEditSN] = useCreateWebsiteTaskMutation();
  const [updateWebEditSN] = useUpdateWebsiteTaskMutation();
  const snCurrentPagePath = typeof window !== "undefined" ? resolvePagePattern(window.location.pathname) : "";

  // Admin-gated to match the BE (the whole /admin/tasks controller is
  // AdminGuard) — a non-admin firing this per page just buys a 403.
  const {
    data: snPageTasks = []
  } = useGetAdminTasksQuery(isAdmin ? {
    pagePath: snCurrentPagePath,
    status: "OPEN"
  } : undefined, {
    skip: !isAdmin
  });
  React.useEffect(() => {
    if (!isAuthenticated) return;
    if (typeof window === "undefined") return;
    if (!snPageTasks.length) return;
    try {
      const url = new URL(window.location.href);
      const reId = url.searchParams.get("reId");
      const reSel = url.searchParams.get("reSel");
      if (!reId && !reSel) return;
      let match;
      if (reId) match = snPageTasks.find(x => x.id === reId);
      if (!match && reSel) match = snPageTasks.find(x => x.elementSelector === reSel);
      if (match) {
        setCaptureData({
          // Carry the id so Save updates the deep-linked task
          // rather than creating a duplicate.
          id: match.id,
          pagePath: match.pagePath,
          sectionLabel: match.label || "",
          elementSelector: match.elementSelector,
          elementText: match.elementText ?? undefined,
          priority: match.priority ?? 2,
          body: match.body ?? ""
        });
        setSearchNavTaskModalOpen(true);
        const el = document.querySelector(match.elementSelector);
        const resolvedEl = el ?? (() => {
          if (match.elementSelector.startsWith('[aria-label="')) {
            const label = match.elementSelector.slice(13, -2);
            return document.querySelector(`[data-select-target="${label}"]`);
          }
          return null;
        })();
        if (resolvedEl) {
          resolvedEl.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
          pcApplyOutline(resolvedEl);
          setTimeout(() => {
            pcClearOutline(resolvedEl);
          }, 1200);
        }
      }
    } catch {}
  }, [isAuthenticated, snPageTasks]);
  const snFindOutlineTarget = React.useCallback(startEl => {
    let node = startEl;
    while (node) {
      const label = node.getAttribute?.("data-select-target");
      if (label && knownSectionLabels.has(label)) return node;
      node = node.parentElement;
    }
    return null;
  }, []);
  const handleCaptureToggle = () => {
    if (!isAuthenticated) return;
    if (captureMode) {
      setCaptureMode(false);
      setTodoListOpen(true);
      if (hoverTarget) {
        try {
          pcClearOutline(hoverTarget);
        } catch {}
      }
      setHoverTarget(null);
      return;
    }
    setCaptureMode(true);
    if (hoverTarget) {
      try {
        pcClearOutline(hoverTarget);
      } catch {}
    }
    setHoverTarget(null);
  };
  React.useEffect(() => {
    if (!captureMode) {
      try {
        pcClearAllOutlines();
      } catch {}
      if (hoverTarget) setHoverTarget(null);
      return;
    }
    const handleOver = e => {
      let el = e.target;
      while (el && el.nodeType !== 1) el = el.parentElement;
      if (!el) return;
      const target = snFindOutlineTarget(el);
      try {
        pcClearAllOutlines();
      } catch {}
      if (!target) {
        setHoverTarget(null);
        return;
      }
      try {
        pcApplyOutline(target);
      } catch {}
      setHoverTarget(target);
    };
    const handleClick = e => {
      e.preventDefault();
      e.stopPropagation();
      const el = e.target;
      if (!el) return;
      const target = snFindOutlineTarget(el);
      if (!target) return;
      const sectionLabel = target.getAttribute("data-select-target") || "";
      const page = resolvePagePattern(window.location.pathname);
      const snippet = (target.textContent || "").trim().slice(0, 120);
      setCaptureData({
        pagePath: page,
        sectionLabel,
        elementSelector: `[data-select-target="${sectionLabel}"]`,
        elementText: snippet,
        priority: 2,
        body: ""
      });
      setCaptureError(null);
      setSearchNavTaskModalOpen(true);
      setCaptureMode(false);
      try {
        pcClearAllOutlines();
      } catch {}
      setHoverTarget(null);
    };
    const handleKeyDown = e => {
      if (e.key === "Escape" || e.keyCode === 27) {
        e.preventDefault();
        e.stopPropagation();
        try {
          pcClearAllOutlines();
        } catch {}
        setHoverTarget(null);
        setCaptureMode(false);
      }
    };
    window.addEventListener("mouseover", handleOver, true);
    window.addEventListener("click", handleClick, true);
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("mouseover", handleOver, true);
      window.removeEventListener("click", handleClick, true);
      window.removeEventListener("keydown", handleKeyDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureMode, hoverTarget, theme.breakpoints.values, snFindOutlineTarget]);
  const navigate = path => {
    if (typeof window !== "undefined") {
      try {
        const href = assetUrl(path);
        window.location.assign(href);
      } catch {
        // noop
      }
    }
  };

  // Newsletter modal
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  // Placeholder autocomplete options for the search input
  const [searchValue, setSearchValue] = useState("");

  // ── Universal site search ──────────────────────────────────────────────
  // Lazily activate the four list queries only after the first search so we
  // don't add four network calls to every page load. RTK Query + the
  // data-version hooks keep the cached data fresh once activated.
  const [searchActivated, setSearchActivated] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [submittedTerm, setSubmittedTerm] = useState("");
  const [searchSections, setSearchSections] = useState([]);

  // Keep cached entity data current while search is active. Any bump of
  // these four entities also changes org content counts, so the counts
  // query is invalidated here — this closes the hard-load race where the
  // counts cache validated against pre-bump version keys.
  const countsDispatch = useAppDispatch();
  const invalidateContentCounts = React.useCallback(() => {
    countsDispatch(api.util.invalidateTags(["OrgContentCounts"]));
  }, [countsDispatch]);
  useDataVersionCheck("organizations", invalidateContentCounts);
  useDataVersionCheck("events", invalidateContentCounts);
  useDataVersionCheck("regulations", invalidateContentCounts);
  useDataVersionCheck("articles", invalidateContentCounts);
  const {
    data: searchOrganizations,
    isFetching: orgsFetching
  } = useGetOrganizationsQuery(undefined, {
    skip: !searchActivated
  });
  const {
    data: searchEvents,
    isFetching: eventsFetching
  } = useGetEventsQuery(undefined, {
    skip: !searchActivated
  });
  const {
    data: searchRegulations,
    isFetching: regulationsFetching
  } = useGetRegulationsQuery(undefined, {
    skip: !searchActivated
  });
  const {
    data: searchArticles,
    isFetching: articlesFetching
  } = useGetArticlesQuery(undefined, {
    skip: !searchActivated
  });
  const searchLoading = searchActivated && (orgsFetching || eventsFetching || regulationsFetching || articlesFetching);

  // Recompute result sections whenever the submitted term changes or the
  // underlying datasets finish loading.
  useEffect(() => {
    if (!searchModalOpen || !submittedTerm) return;
    if (searchLoading) return;
    setSearchSections(searchSite(submittedTerm, {
      organizations: searchOrganizations,
      events: searchEvents,
      regulations: searchRegulations,
      articles: searchArticles
    }, {
      eventsVisible: siteSettings.eventsVisible,
      regulationsVisible: siteSettings.regulationsVisible,
      articlesVisible: siteSettings.articlesVisible
    }));
  }, [searchModalOpen, submittedTerm, searchLoading, searchOrganizations, searchEvents, searchRegulations, searchArticles, siteSettings.eventsVisible, siteSettings.regulationsVisible, siteSettings.articlesVisible]);
  const runSearch = () => {
    const term = searchValue.trim();
    if (!term) return;
    setSearchActivated(true);
    setSubmittedTerm(term);
    setSearchSections([]);
    setSearchModalOpen(true);
  };

  // Already normalized by SiteSettingsContext: retired values
  // ("original"/"center") and unset both arrive as "lower-right".
  const helpBtnPos = siteSettings.helpButtonPosition;
  /** True for both lower-right variants — they share every layout rule
   *  except where the newsletter/cert-help buttons live. */
  const isLowerRight = helpBtnPos === "lower-right" || helpBtnPos === "lower-right-2";

  // Newsletter button — shared element reused in lower-right layouts.
  // lower-right-2 drops the bell icon (the button sits inside the
  // account cluster, where the icon reads as noise).
  const makeNewsletterButton = withIcon => /*#__PURE__*/_jsx(Button, {
    size: "small",
    onClick: () => setNewsletterOpen(true),
    startIcon: withIcon ? /*#__PURE__*/_jsx(NotificationsIcon, {
      fontSize: "small",
      sx: {
        color: common.white
      }
    }) : undefined,
    sx: {
      backgroundColor: blue.primary,
      color: common.white,
      borderRadius: 1,
      px: 1.5,
      py: 0.5,
      textTransform: "none",
      fontWeight: 500,
      fontSize: "0.875rem",
      "&:hover": {
        backgroundColor: blue.dark
      },
      minWidth: 0
    },
    children: "Newsletter"
  });
  const newsletterButton = makeNewsletterButton(true);

  // Account / sign-in / admin selector buttons — shared cluster reused in lower-right layout
  // Both utility bars now use a white background (matching the main
  // nav row), so the account cluster always renders in its light-bg
  // treatment (dark text) regardless of help-button position.
  const onLightBg = true;
  const accountCluster = /*#__PURE__*/_jsxs(Box, {
    sx: {
      display: "flex",
      alignItems: "center",
      gap: 0.5
    },
    children: [isAdmin && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(IconButton, {
        size: "small",
        onClick: handleCaptureToggle,
        "aria-label": "toggle capture tasks",
        sx: {
          color: captureMode ? yellow.primary : onLightBg ? gray.dark : common.white,
          "&:hover": {
            backgroundColor: onLightBg ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.12)"
          }
        },
        children: /*#__PURE__*/_jsx(EditIcon, {
          fontSize: "small"
        })
      }), todoListEnabled && /*#__PURE__*/_jsx(IconButton, {
        size: "small",
        onClick: () => setTodoListOpen(p => !p),
        "aria-label": "toggle todo list",
        sx: {
          color: onLightBg ? gray.dark : common.white,
          "&:hover": {
            backgroundColor: onLightBg ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.12)"
          }
        },
        children: /*#__PURE__*/_jsx(Badge, {
          badgeContent: todoUnresolved,
          color: "warning",
          children: /*#__PURE__*/_jsx(TocIcon, {
            fontSize: "small"
          })
        })
      })]
    }), isAuthenticated ? /*#__PURE__*/_jsxs(Button, {
      size: "small",
      onClick: accountMenu.openMenu,
      "aria-label": "account menu",
      endIcon: /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center",
          gap: 0.5
        },
        children: [/*#__PURE__*/_jsx(Badge, {
          color: "error",
          badgeContent: accountMenu.notifCount,
          children: /*#__PURE__*/_jsx(Avatar, {
            src: user?.profileImageUrl || undefined,
            sx: {
              width: 24,
              height: 24
            },
            children: /*#__PURE__*/_jsx(AccountCircle, {
              sx: {
                width: 20,
                height: 20
              }
            })
          })
        }), accountMenu.profileMenuOpen ? /*#__PURE__*/_jsx(ExpandLessIcon, {
          fontSize: "small"
        }) : /*#__PURE__*/_jsx(ExpandMoreIcon, {
          fontSize: "small"
        })]
      }),
      sx: {
        color: onLightBg ? gray.dark : common.white,
        textTransform: "none",
        fontSize: "0.875rem",
        fontWeight: 500,
        px: 0.5,
        minWidth: 0,
        "&:hover": {
          backgroundColor: onLightBg ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)"
        }
      },
      children: [/*#__PURE__*/_jsxs(Box, {
        component: "span",
        sx: {
          display: {
            xs: "none",
            md: "inline"
          }
        },
        children: [snUserName.first, snUserName.last ? ` ${snUserName.last}` : ""]
      }), /*#__PURE__*/_jsx(Box, {
        component: "span",
        sx: {
          display: {
            xs: "inline",
            md: "none"
          }
        },
        children: snUserName.first
      })]
    }) : /*#__PURE__*/_jsx(Button, {
      size: "small",
      onClick: () => navigate("/login"),
      sx: {
        color: onLightBg ? gray.dark : common.white,
        textTransform: "none",
        fontSize: "0.875rem",
        fontWeight: 500,
        "&:hover": {
          backgroundColor: onLightBg ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)"
        }
      },
      children: "Sign in / Sign up"
    })]
  });

  // Shared "Get Certification Help" button used across all positions
  // lower-right-2 drops the "Get" so the label fits the account cluster.
  const makeCertHelpButton = label => /*#__PURE__*/_jsx(Button, {
    size: "small",
    onClick: () => navigate("/help"),
    sx: {
      backgroundColor: green.primary,
      color: common.white,
      borderRadius: 1,
      px: 1.5,
      py: 0.5,
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.875rem",
      "&:hover": {
        backgroundColor: green.dark
      },
      whiteSpace: "nowrap"
    },
    children: label
  });
  const certHelpButton = makeCertHelpButton("Get Certification Help");

  // Match the legacy NavBar's primary-link styling
  const navLinkSx = (color, hover) => ({
    flex: 0,
    textAlign: "center",
    whiteSpace: "nowrap",
    px: 1.25,
    "& a": {
      color,
      fontWeight: 600,
      textDecoration: "none"
    },
    "& a:hover": {
      color: hover
    }
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("style", {
      children: `
            [data-pc-hover-promote-z="1"],
            [data-pc-re-outline="1"]:not([data-pc-no-promote-z]) {
                z-index: 100 !important;
            }
            [data-pc-hover-outline="1"]::after,
            [data-pc-re-outline="1"]::after {
                content: '';
                position: absolute;
                inset: 0;
                border: 5px dashed gold;
                pointer-events: none;
                z-index: 2147483646;
            }
        `
    }), /*#__PURE__*/_jsxs(AppBar, {
      position: "fixed",
      color: "default",
      sx: {
        backgroundColor: "white",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
      },
      "data-pc-outline": "navbar",
      "data-select-target": "Navigation Bar",
      children: [!isLowerRight && /*#__PURE__*/_jsxs(Box, {
        sx: {
          backgroundColor: common.white,
          color: gray.dark,
          px: {
            xs: 1.5,
            md: 3
          },
          py: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 36,
          position: "relative"
        },
        children: [/*#__PURE__*/_jsxs(Box, {
          sx: {
            display: "flex",
            alignItems: "center",
            gap: 1
          },
          children: [newsletterButton, (helpBtnPos === "upper-left" || helpBtnPos === "upper-left-2") && certHelpButton]
        }), helpBtnPos !== "upper-left-2" && accountCluster]
      }), /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: {
            xs: 1.5,
            md: 2
          },
          px: {
            xs: 1.5,
            md: 3
          },
          py: 1.5,
          backgroundColor: "white",
          flexWrap: {
            xs: "wrap",
            md: "nowrap"
          }
        },
        children: [/*#__PURE__*/_jsxs(Box, {
          component: Link,
          href: "/",
          sx: {
            display: "flex",
            alignItems: "center",
            gap: 1,
            textDecoration: "none",
            flex: "0 0 auto"
          },
          children: [/*#__PURE__*/_jsx(Box, {
            component: "img",
            src: assetUrl("/PlaceholderImgs/checkLogo.png"),
            alt: "",
            sx: {
              height: 40,
              width: "auto",
              display: "block"
            }
          }), /*#__PURE__*/_jsx(Typography, {
            component: "span",
            sx: {
              color: green.primary,
              fontWeight: 700,
              fontSize: {
                xs: "0.85rem",
                sm: "1.05rem",
                md: "1.05rem",
                lg: "1.35rem",
                xl: "2rem"
              },
              whiteSpace: "nowrap"
            },
            children: "ProductCertification.com"
          })]
        }), !isLowerRight && /*#__PURE__*/_jsx(SearchBar, {
          scope: "all",
          mode: "manual",
          value: searchValue,
          onValueChange: setSearchValue,
          onSearch: () => runSearch(),
          sx: {
            flex: "0 0 auto"
          }
        }), isLowerRight && /*#__PURE__*/_jsx(SearchBar, {
          scope: "all",
          mode: "manual",
          value: searchValue,
          onValueChange: setSearchValue,
          onSearch: () => runSearch()
        })]
      }), /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center",
          justifyContent: isLowerRight || helpBtnPos === "upper-left-2" ? "space-between" : undefined,
          gap: 1,
          px: {
            xs: 1.5,
            md: 3
          },
          py: 0.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: "white",
          flexWrap: "wrap"
        },
        children: [/*#__PURE__*/_jsx(Box, {
          sx: {
            display: "flex",
            alignItems: "center"
          },
          children: /*#__PURE__*/_jsxs(List, {
            sx: {
              display: "flex",
              flexDirection: "row",
              p: 0
            },
            children: [/*#__PURE__*/_jsx(ListItem, {
              sx: navLinkSx(gray.dark, gray.primary),
              children: /*#__PURE__*/_jsx(Link, {
                href: "/",
                children: "Home"
              })
            }), /*#__PURE__*/_jsx(ListItem, {
              sx: navLinkSx(orange.primary, orange.light),
              children: /*#__PURE__*/_jsx(Link, {
                href: "/organizations",
                children: "Organizations"
              })
            }), siteSettings.eventsVisible && /*#__PURE__*/_jsx(ListItem, {
              sx: navLinkSx(green.primary, green.light),
              children: /*#__PURE__*/_jsx(Link, {
                href: "/events",
                children: "Events"
              })
            }), siteSettings.regulationsVisible && /*#__PURE__*/_jsx(ListItem, {
              sx: navLinkSx(blue.primary, blue.light),
              children: /*#__PURE__*/_jsx(Link, {
                href: "/regulations",
                children: "Regulations"
              })
            }), siteSettings.articlesVisible && /*#__PURE__*/_jsx(ListItem, {
              sx: navLinkSx(purple.primary, purple.light),
              children: /*#__PURE__*/_jsx(Link, {
                href: "/articles",
                children: "Articles"
              })
            }), siteSettings.professionalsVisible && /*#__PURE__*/_jsx(ListItem, {
              sx: navLinkSx(rose.primary, rose.light),
              children: /*#__PURE__*/_jsx(Link, {
                href: "/professionals",
                children: "Professionals"
              })
            })]
          })
        }), (helpBtnPos === "lower-right" || helpBtnPos === "upper-left-2") && accountCluster, helpBtnPos === "lower-right-2" && /*#__PURE__*/_jsxs(Box, {
          sx: {
            display: "flex",
            // row-reverse: DOM order [account, news/help]
            // renders as [news/help | account] so the
            // account dropdown stays FAR RIGHT, while
            // news/help remains the trailing group and
            // wraps to the next line first. Wrapped
            // lines start from main-start = the right
            // edge, so they stay right-aligned.
            flexDirection: "row-reverse",
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "flex-start",
            columnGap: 1,
            rowGap: 0.5,
            // Row 3 is justify-content: space-between; a
            // lone item on a wrapped line sits at flex-
            // START under space-between, so the cluster
            // landed on the LEFT when it wrapped below
            // the nav links. An auto left margin absorbs
            // the line's free space and keeps the
            // cluster on the right edge of whichever
            // line it lands on.
            ml: "auto"
          },
          children: [accountCluster, /*#__PURE__*/_jsxs(Box, {
            sx: {
              display: "flex",
              alignItems: "center",
              gap: 1
            },
            children: [makeNewsletterButton(false), makeCertHelpButton("Certification Help")]
          })]
        })]
      }), helpBtnPos === "lower-right" && /*#__PURE__*/_jsxs(Box, {
        sx: {
          backgroundColor: common.white,
          color: gray.dark,
          px: {
            xs: 1.5,
            md: 3
          },
          py: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          minHeight: 36,
          gap: 1
        },
        children: [newsletterButton, certHelpButton]
      }), /*#__PURE__*/_jsx(SiteSearchModal, {
        open: searchModalOpen,
        onClose: () => setSearchModalOpen(false),
        term: submittedTerm,
        sections: searchSections,
        loading: searchLoading
      }), /*#__PURE__*/_jsx(Dialog, {
        open: newsletterOpen,
        onClose: () => setNewsletterOpen(false),
        maxWidth: false,
        fullWidth: true,
        PaperProps: {
          sx: {
            overflow: "hidden",
            width: "100vw",
            maxWidth: "100vw",
            m: 0,
            height: "25vh",
            minHeight: "300px"
          }
        },
        children: /*#__PURE__*/_jsxs(Box, {
          sx: {
            position: "relative",
            backgroundColor: "#2c4a63",
            backgroundImage: `url(${assetUrl("/PlaceholderImgs/LabBanner.jpeg")})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: "300px"
          },
          children: [/*#__PURE__*/_jsx(Box, {
            sx: {
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.55)",
              zIndex: 1
            }
          }), /*#__PURE__*/_jsx(Box, {
            sx: {
              position: "relative",
              zIndex: 2,
              px: 2,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center"
            },
            children: /*#__PURE__*/_jsxs(Box, {
              sx: {
                position: "relative",
                width: "30vw",
                // 325px floor, but never wider than the
                // viewport so it can't overflow on phones.
                minWidth: "min(325px, 100%)",
                maxWidth: "100%",
                my: "8px",
                p: 1.5,
                borderRadius: 2,
                backgroundColor: "rgba(38,38,38,0.9)",
                display: "flex",
                flexDirection: "column",
                gap: 1
              },
              children: [/*#__PURE__*/_jsx(IconButton, {
                onClick: () => setNewsletterOpen(false),
                size: "small",
                "aria-label": "Close newsletter signup",
                sx: {
                  position: "absolute",
                  top: 4,
                  right: 4,
                  zIndex: 1,
                  color: common.white,
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.15)"
                  }
                },
                children: /*#__PURE__*/_jsx(CloseIcon, {
                  fontSize: "small"
                })
              }), /*#__PURE__*/_jsxs(Typography, {
                variant: "h2",
                sx: {
                  color: common.white,
                  fontWeight: 700,
                  textAlign: "center",
                  // The h2 variant injects responsive
                  // (media-query) font sizes that override a
                  // plain `fontSize`. Override per breakpoint
                  // so the heading actually shrinks.
                  fontSize: {
                    xs: "1.5rem",
                    sm: "1.5rem",
                    md: "1.5rem",
                    lg: "1.5rem"
                  }
                },
                children: ["Stay Up to Date with", /*#__PURE__*/_jsx("br", {}), "Regulatory Changes"]
              }), /*#__PURE__*/_jsx(Typography, {
                variant: "body1",
                sx: {
                  color: "rgba(255,255,255,0.85)",
                  textAlign: "center",
                  // Allow wrapping on the smallest screens
                  // so the line can't clip/overflow.
                  whiteSpace: {
                    xs: "normal",
                    sm: "nowrap"
                  },
                  // body1 variant uses responsive
                  // (media-query) sizes that override a
                  // plain fontSize — set per breakpoint.
                  fontSize: {
                    xs: "0.8rem",
                    sm: "0.8rem",
                    md: "0.8rem",
                    lg: "0.8rem"
                  }
                },
                children: "Subscribe to the ProductCertification.com newsletter."
              }), /*#__PURE__*/_jsx(Paper, {
                elevation: 3,
                sx: {
                  p: 2,
                  borderRadius: 1
                },
                children: /*#__PURE__*/_jsx(NewsletterForm, {
                  source: "navbar",
                  column: true,
                  placeholder: "Enter your email...",
                  showUnsubscribeNote: true
                })
              })]
            })
          })]
        })
      }), accountMenu.elements, /*#__PURE__*/_jsx(TaskModal, {
        open: taskModalOpen,
        onClose: () => {
          setSearchNavTaskModalOpen(false);
          setCaptureData(null);
          setCaptureError(null);
        },
        mode: "capture",
        initialData: captureData || undefined,
        saving: captureSaving,
        error: captureError,
        onSave: async data => {
          setCaptureSaving(true);
          setCaptureError(null);
          try {
            if (data.id) {
              // Deep-linked existing task: update, don't
              // create a duplicate.
              await updateWebEditSN({
                id: data.id,
                label: data.sectionLabel,
                priority: data.priority,
                body: data.body.trim() || undefined,
                subtasks: data.subtasks
              }).unwrap();
            } else {
              await createWebEditSN({
                pagePath: data.pagePath,
                elementSelector: data.elementSelector,
                elementText: data.elementText || "",
                label: data.sectionLabel,
                priority: data.priority,
                body: data.body.trim() || undefined,
                screenshots: data.screenshots?.map(s => ({
                  thumbnailUrl: s.thumbnailUrl,
                  mediumUrl: s.mediumUrl,
                  originalUrl: s.originalUrl
                })),
                subtasks: data.subtasks,
                attachments: data.attachments
              }).unwrap();
            }
            setSearchNavTaskModalOpen(false);
            setCaptureData(null);
          } catch (err) {
            setCaptureError(err?.data?.message || err?.message || "Failed to create task");
          } finally {
            setCaptureSaving(false);
          }
        },
        onUploadScreenshot: async file => {
          const res = await uploadImageSN({
            file,
            folder: "tasks",
            entityType: "task-screenshot"
          }).unwrap();
          return {
            thumbnailUrl: res.thumbnail,
            mediumUrl: res.medium,
            originalUrl: res.original
          };
        },
        onUploadFile: async file => {
          return uploadFileSN({
            file,
            folder: "tasks"
          }).unwrap();
        }
      }), /*#__PURE__*/_jsx(TodoList, {
        open: todoListOpen,
        onClose: () => setTodoListOpen(false)
      })]
    })]
  });
}

// ---------------------------------------------------------------------------
// Navigation — master nav component. Picks between SearchNavBar and the
// legacy NavBar based on the `search` prop, or — when the prop is omitted —
// the `searchNavVisible` site setting toggled from the Admin Promotions tab.
// ---------------------------------------------------------------------------

// Pages that render a Sidenav on the left at `lg` breakpoint and up. The
// navbar gets shifted right by the sidebar width on those routes so it does
// not sit underneath the permanent drawer.
const SIDENAV_PATHNAME_RE = /^\/(regulations|articles)(\/|$)/;
const SIDENAV_WIDTH = 320;
export default function Navigation({
  search
} = {}) {
  const theme = useTheme();
  const {
    settings,
    loading: settingsLoading
  } = useSiteSettings();

  // `resolvedSearchNav` is seeded from the localStorage cache before first
  // paint (useLayoutEffect). The live API value from settings context takes
  // over as soon as it arrives and is always the authoritative source.
  const [cachedSearchNav, setCachedSearchNav] = useState(null);
  useLayoutEffect(() => {
    try {
      const raw = localStorage.getItem("pc_site_settings_v1");
      if (raw) {
        const cached = JSON.parse(raw);
        if ("searchNavVisible" in cached) {
          setCachedSearchNav(cached.searchNavVisible === true);
        }
      }
    } catch {}
  }, []);

  // Once settings have loaded from the API, always use that value (it is
  // fresher than the localStorage snapshot and is the source of truth).
  // Fall back to the localStorage cache while the first request is in-flight.
  const resolvedSearchNav = !settingsLoading ? settings.searchNavVisible : cachedSearchNav ?? false;
  const useSearch = search ?? resolvedSearchNav;

  // Sidenav becomes permanent (always open, non-collapsible) at `lg` and up
  // — mirror the Sidenav component's `permanentBreakpoint="lg"` default.
  const isSidenavPermanent = useMediaQuery(theme.breakpoints.up("lg"));

  // Track the current pathname on the client so we know whether a Sidenav
  // is present on this route. Avoid `useRouter()` to stay safe in static
  // export contexts where the router may not be mounted.
  const [pathname, setPathname] = useState("");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => setPathname(window.location.pathname);
    update();
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  // The route only says a Sidenav *could* be here; whether one actually
  // renders is an admin setting. Shifting on the pathname alone left the
  // navbar pushed 320px right with nothing beside it once the nav was
  // toggled off.
  const regulationsSideNav = useIndexSideNav("regulations");
  const articlesSideNav = useIndexSideNav("articles");
  const routeHasSidenav = SIDENAV_PATHNAME_RE.test(pathname);
  // Route classes differ: the INDEX pages honor the admin side-nav
  // toggle; DETAIL pages always render their section sidenav (the
  // toggle only governs the index), so the navbar must always shift
  // beside a permanent detail sidenav or it renders underneath it.
  // Create pages have no sidenav at all.
  const isIndexRoute = /^\/(regulations|articles)\/?$/.test(pathname);
  const isCreateRoute = /^\/(regulations|articles)\/create(\/|$)/.test(pathname);
  const sidenavEnabledHere = isCreateRoute ? false : isIndexRoute ? /^\/regulations/.test(pathname) ? regulationsSideNav : articlesSideNav : true;
  const hasSidenav = routeHasSidenav && sidenavEnabledHere;
  const shiftLeft = hasSidenav && isSidenavPermanent;

  // Measure the rendered AppBar so the spacer below it always matches the
  // current variant's height (legacy ≈ 64px, SearchNavBar ≈ 170–200px and
  // wraps on small viewports).
  const wrapperRef = useRef(null);
  const [navHeight, setNavHeight] = useState(useSearch ? 170 : 64);
  // Layout effect + a SYNCHRONOUS first measurement: the real AppBar
  // height lands before paint, so the spacer never paints a frame at the
  // estimate and then shifts the page down (the org-detail "header cut
  // off on mount" glitch). The ResizeObserver stays for later wraps and
  // window resizes. (SSR renders the estimate; effects don't run there,
  // and hydration matches that HTML before this corrects it pre-paint.)
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const appBar = wrapper?.querySelector(".MuiAppBar-root");
    if (!appBar) {
      // Variant switched but nothing to measure yet — fall back to
      // the estimate so there is no stale-height frame.
      setNavHeight(useSearch ? 170 : 64);
      return;
    }
    setNavHeight(appBar.getBoundingClientRect().height);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setNavHeight(entry.contentRect.height);
    });
    ro.observe(appBar);
    return () => ro.disconnect();
  }, [useSearch]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Box, {
      ref: wrapperRef,
      sx: {
        "& > .MuiAppBar-root": {
          left: shiftLeft ? `${SIDENAV_WIDTH}px` : 0,
          width: shiftLeft ? `calc(100% - ${SIDENAV_WIDTH}px)` : "100%",
          transition: theme.transitions.create(["left", "width"], {
            duration: theme.transitions.duration.short
          })
        }
      },
      children: useSearch ? /*#__PURE__*/_jsx(SearchNavBar, {}) : /*#__PURE__*/_jsx(NavBar, {})
    }), /*#__PURE__*/_jsx(Box, {
      "aria-hidden": true,
      sx: {
        height: navHeight
      }
    })]
  });
}